/* eslint-disable no-underscore-dangle */
import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal, Table, Button, Form, Badge,
} from 'react-bootstrap';
import OperatorService from '../../../Services/OperatorService';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import { expenseAccounts } from '../../../Utils/operatorType';
import {
  EXPENSE_COST_TYPES,
  COST_TYPE_LABELS,
  byId,
  costOperatorsOf,
  costOperatorsPayload,
  costTypeNote,
  describeCostOperators,
  withCostOperator,
} from '../../../Utils/carCostOperators';

/** One line, above the table, because the column heading cannot say this. */
const ROUTING_EXPLAINER = 'Expense routing decides where a driver’s receipt for a vehicle is '
  + 'charged: the cost is filed against the account picked here, and a type with no account '
  + 'goes to the operator on the trip.';

/**
 * Where this vehicle's costs go, when the row is not being edited.
 *
 * Says where they go rather than leaving the cell empty: an unrouted vehicle
 * is the normal, correct state for anything third-party, and a blank cell
 * reads as missing data instead of as the answer. That answer is the
 * operator on the trip, because the approve screen defaults an unrouted
 * receipt to it. Until 2026-11-02 this said "costs absorbed", which was the
 * default then. Why it changed is in `Utils/expenseCostDestination`.
 */
function CostRoutingSummary({ car, operatorsById }) {
  const routes = describeCostOperators(car, operatorsById);

  if (routes.length === 0) {
    return <span className="text-muted">Not routed — costs go to the trip’s operator</span>;
  }

  return routes.map((route) => (
    <div key={route.costType} className="small">
      <span className="text-muted">{`${route.label}: `}</span>
      {route.name}
    </div>
  ));
}

function FleetManagementModal({
  operator, operators = [], show = false, handleClose,
}) {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  /**
   * The vehicles whose routing was CHANGED on this screen — not the ones
   * that have a mapping.
   *
   * The API reads an absent `cost_operators` as "leave it alone" and a
   * present one as the vehicle's whole mapping from now on, so an ordinary
   * edit has to omit the field and only a deliberate change may send it.
   * Tracking the change (rather than sending whatever was loaded) means a
   * plate correction cannot blank a map even if the list endpoint one day
   * stops returning one. See `Utils/carCostOperators`.
   */
  const [routingChanged, setRoutingChanged] = useState([]);
  const [formData, setFormData] = useState({
    type: '',
    color: '',
    plate_number: '',
    active: 1,
  });

  /**
   * Only cost buckets, never a supplier: routing a parking cost to a real
   * supplier files it as money we owe them, and it gets paid.
   */
  const costAccounts = useMemo(() => expenseAccounts(operators), [operators]);
  const operatorsById = useMemo(() => byId(operators), [operators]);

  const fetchCars = async () => {
    if (!operator?.Id) return;

    setLoading(true);
    try {
      const response = await OperatorService.fetchCars(operator.Id);
      setCars(response.data?.data || []);
      setRoutingChanged([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch cars');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && operator?.Id) {
      fetchCars();
    }
  }, [show, operator]);

  const handleChange = (e) => {
    const {
      id, value, type, checked,
    } = e.target;
    // Remove 'add_' prefix from controlId to get actual field name
    const fieldName = id.replace('add_', '');
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));
  };

  const resetForm = () => {
    setFormData({
      type: '',
      color: '',
      plate_number: '',
      active: 1,
    });
    setValidationErrors([]);
    setError('');
  };

  const handleAddCar = async () => {
    setLoading(true);
    try {
      await OperatorService.addCar({
        ...formData,
        operator_id: operator.Id,
        active: Number(formData.active),
      });
      await fetchCars();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add car');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCar = async (car) => {
    setLoading(true);
    try {
      /**
       * Enumerated, never spread. A field added by spreading the row would
       * put `cost_operators` on the body as the LIST endpoint shaped it, and
       * the controller would then rewrite the whole map from it on every
       * plate correction. `FleetCostOperators.test.jsx` fails if that ever
       * happens: an edit that did not touch the routing must send no
       * `cost_operators` key at all.
       */
      const payload = {
        Id: car.Id,
        type: car.type,
        color: car.color,
        plate_number: car.plate_number,
        operator_id: operator.Id,
        active: Number(car.active),
      };

      // Present ONLY when this screen changed it — and then complete, since
      // the API replaces rather than merges. A cleared type rides along as ''.
      if (routingChanged.includes(car.Id)) {
        payload.cost_operators = costOperatorsPayload(car);
      }

      await OperatorService.addCar(payload);
      await fetchCars();
      setEditingCarId(null);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update car');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCar = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to deactivate this car?')) {
      return;
    }

    setLoading(true);
    try {
      await OperatorService.deleteCar(id);
      await fetchCars();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete car');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (car) => {
    setEditingCarId(car.Id);
  };

  const cancelEditing = () => {
    setEditingCarId(null);
    fetchCars();
  };

  const handleEditChange = (carId, field, value) => {
    setCars((prevCars) => prevCars.map((c) => (c.Id === carId ? { ...c, [field]: value } : c)));
  };

  /**
   * Picking an account, or picking the blank option to clear one. Both are
   * a CHANGE, so both arm `routingChanged` — clearing the only mapping a
   * vehicle has would otherwise send nothing and silently do nothing.
   */
  const handleCostOperatorChange = (carId, costType, operatorId) => {
    setCars((prevCars) => prevCars.map((c) => (
      c.Id === carId ? { ...c, cost_operators: withCostOperator(c, costType, operatorId) } : c
    )));
    setRoutingChanged((prev) => (prev.includes(carId) ? prev : [...prev, carId]));
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Fleet Management -
          {' '}
          {operator?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Cars</h6>
          {!showAddForm && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddForm(true)}
              disabled={loading}
            >
              <i className="fa fa-plus me-2" />
              Add New Car
            </Button>
          )}
        </div>

        {showAddForm && (
          <div className="card mb-3 bg-light">
            <div className="card-body">
              <h6 className="card-title">Add New Car</h6>
              <Form>
                <div className="row">
                  <Form.Group className="mb-2 col-md-3" controlId="add_type">
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.type}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="type" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_color">
                    <Form.Label>Color</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.color}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="color" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_plate_number">
                    <Form.Label>Plate Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.plate_number}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="plate_number" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_active">
                    <Form.Label>Active *</Form.Label>
                    <Form.Select
                      value={formData.active}
                      onChange={handleChange}
                      size="sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddCar}
                    disabled={loading}
                  >
                    <i className="fa fa-check me-2" />
                    Save
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        )}

        <p className="text-muted small mb-2">{ROUTING_EXPLAINER}</p>

        <Table striped bordered hover size="sm" responsive className="fleet-driver-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Color</th>
              <th>Plate Number</th>
              <th>Active</th>
              <th style={{ width: '230px' }}>Expense routing</th>
              <th style={{ width: '150px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cars.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No cars found
                </td>
              </tr>
            )}
            {cars.map((car) => (
              <tr
                key={car.Id}
                className={car.active === 0 ? 'text-muted' : ''}
                style={{ opacity: car.active === 0 ? 0.6 : 1 }}
              >
                {editingCarId === car.Id ? (
                  <>
                    <td data-label="Type">
                      <Form.Control
                        type="text"
                        value={car.type || ''}
                        onChange={(e) => handleEditChange(car.Id, 'type', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td data-label="Color">
                      <Form.Control
                        type="text"
                        value={car.color || ''}
                        onChange={(e) => handleEditChange(car.Id, 'color', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td data-label="Plate">
                      <Form.Control
                        type="text"
                        value={car.plate_number || ''}
                        onChange={(e) => handleEditChange(car.Id, 'plate_number', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td data-label="Active">
                      <Form.Select
                        value={car.active}
                        onChange={(e) => handleEditChange(car.Id, 'active', Number(e.target.value))}
                        size="sm"
                      >
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </Form.Select>
                    </td>
                    <td data-label="Expense routing">
                      {costAccounts.length === 0 && (
                        <div className="text-muted small">
                          No expense accounts exist yet — create one on the
                          operators list first.
                        </div>
                      )}
                      {EXPENSE_COST_TYPES.map((costType) => {
                        const note = costTypeNote(costType);
                        const label = `${COST_TYPE_LABELS[costType]} account`;

                        return (
                          <div className="mb-1" key={costType}>
                            <Form.Label className="mb-0 small text-muted">
                              {COST_TYPE_LABELS[costType]}
                              {note && (
                                <span className="fst-italic">
                                  {` (${note})`}
                                </span>
                              )}
                            </Form.Label>
                            <Form.Select
                              aria-label={`${label} for ${car.plate_number || car.Id}`}
                              value={costOperatorsOf(car)[costType] || ''}
                              onChange={(e) => handleCostOperatorChange(
                                car.Id,
                                costType,
                                e.target.value,
                              )}
                              size="sm"
                            >
                              {/* Clearing has to be reachable, so the blank
                                  option is always first and always present. */}
                              <option value="">Not routed — the trip’s operator</option>
                              {costAccounts.map((account) => (
                                <option key={account.Id} value={account.Id}>
                                  {account.name}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        );
                      })}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleUpdateCar(car)}
                          disabled={loading}
                        >
                          <i className="fa fa-check" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={cancelEditing}
                          disabled={loading}
                        >
                          <i className="fa fa-times" />
                        </Button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label="Type">{car.type || '-'}</td>
                    <td data-label="Color">{car.color || '-'}</td>
                    <td data-label="Plate">{car.plate_number || '-'}</td>
                    <td data-label="Active">
                      {car.active === 1 ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="danger">Inactive</Badge>
                      )}
                    </td>
                    <td data-label="Expense routing">
                      <CostRoutingSummary car={car} operatorsById={operatorsById} />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => startEditing(car)}
                          disabled={loading}
                        >
                          <i className="fa fa-edit" />
                        </Button>
                        {car.active === 1 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteCar(car.Id)}
                            disabled={loading}
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default FleetManagementModal;
